/* eslint func-style: "off" */
import { all, call, fork, put, select, takeLatest } from 'redux-saga/effects';
import {
  FETCH_ESTIMATE,
  fetchEstimateBegan,
  fetchEstimateSucceeded,
  fetchEstimateFailed
} from './HistoricalImport.ducks';
import { FETCH_IMPORT_HISTORY, fetchImportHistorySucceeded, fetchImportHistoryFailed } from './HistoricalImport.ducks';
import { IMPORT_STORIES, importStoriesSucceeded, importStoriesFailed } from './HistoricalImport.ducks';
import apiClient from 'helpers/apiClient';
import queryString from 'querystring';
import { convertDatePickerTimeToApiTime } from 'helpers/timeFormat';
import get from 'lodash/get';

const getUserId = (state) => state.AppUser?.user?.id;
const _getFetchParams = (timezoneOffset) => {
  return (state) => {
    const params = {
      start_time: convertDatePickerTimeToApiTime(state.HistoricalImport.selectedStartDate, timezoneOffset),
      end_time: convertDatePickerTimeToApiTime(state.HistoricalImport.selectedEndDate, timezoneOffset),
      profiles: state.HistoricalImport.selectedProfiles,
      sources: state.HistoricalImport.selectedSources,
      user_id: getUserId(state)
    };
    if (state.HistoricalImport.estimate !== null) {
      params.story_count = state.HistoricalImport.estimate;
    }
    return params;
  };
};

const handleFetchEstimateError = (event) => {
  // 504 Gateway Timeout — the estimate service took too long to respond
  if (get(event, 'response.status') === 504) {
    return fetchEstimateFailed('Error Calculating Estimate. Please Try Again Later.');
  }

  const errorMessage = 'Error retrieving estimate.';
  const errors = get(event, 'body.errors', []);
  if (errors.length === 0) {
    return fetchEstimateFailed(errorMessage);
  }

  const firstErrorMessage = errors[0];

  /**
   * Usually if there is a number in the error message it goes something like this:
   * "Profiles: size [30] is not between 1 and 20."
   * Here 30 is the profiles number tried to import, and 20 the limit.
   * We want the 20 found at the third position of the array.
   */
  const getDigitsRegex = /\d+/g;
  const detectErrorRegex =
    /(.*POST)(.*error)(.*root_cause)(.*\"status\":400).*/gm;
  const numbersFoundInErrorMessage =
    firstErrorMessage.match(getDigitsRegex) || [];
  const profilesLimit = numbersFoundInErrorMessage.length > 0 ? numbersFoundInErrorMessage[2] : null;

  if (detectErrorRegex.test(firstErrorMessage)) {
    return fetchEstimateFailed(errorMessage);
  } else if (profilesLimit) {
    return fetchEstimateFailed(`Too many datasets selected. Please select ${profilesLimit} or fewer datasets.`);
  }

  return fetchEstimateFailed(firstErrorMessage);
};

const fetchEstimate = function* (action) {
  try {
    const campaignId = action.campaignId;
    const fetchParams = yield select(_getFetchParams(action.timezoneOffset));
    const query = queryString.stringify(fetchParams);
    yield put(fetchEstimateBegan());
    const payload = yield call(apiClient, `/delorean/campaigns/${campaignId}/imports/estimate?${query}`);
    yield put(fetchEstimateSucceeded(payload, action.trackBaselineHistoric));
  } catch (e) {
    yield put(handleFetchEstimateError(e));
  }
};

// GET /campaigns/:id/imports?limit=X (defaults to 10)
//
// 200 OK
// {
//     "imports": [
//     {
//         "profiles":["ID"],
//         "gte":"2017-04-19T00:00:00Z",
//         "lt":"2017-04-20T00:00:00Z",
//         "sources":["twitter", "blog"],
//         "status": "Done",
//         "updated_at": "2017-06-07T01:00:00Z",
//         "id": "3456789987654",
//         ...
//     },
//     ...
//     ]
// }
const fetchImportHistory = function* (action) {
  try {
    const campaignId = action.campaignId;
    const payload = yield call(apiClient, `/delorean/campaigns/${campaignId}/imports?limit=3`);
    yield put(fetchImportHistorySucceeded(payload));
  } catch (e) {
    yield put(fetchImportHistoryFailed('Error fetching import history.'));
  }
};

const importStories = function* (action) {
  try {
    const campaignId = action.campaignId;
    const requestBody = yield select(_getFetchParams(action.timezoneOffset));
    // only add user_id to the body for the POST
    const userId = yield select(getUserId);
    requestBody.user_id = userId;

    const fetchOptions = {
      method: 'POST',
      body: JSON.stringify(requestBody)
    };
    const payload = yield call(apiClient, `/delorean/campaigns/${campaignId}/imports`, fetchOptions);
    yield put(importStoriesSucceeded(payload));
  } catch (e) {
    yield put(importStoriesFailed('Unable to import stories due to a server error.'));
  }
};


const fetchEstimateSaga = function* () {
  yield takeLatest( FETCH_ESTIMATE, fetchEstimate);
};

const fetchImportHistorySaga = function* () {
  yield takeLatest( FETCH_IMPORT_HISTORY, fetchImportHistory);
};

const importStoriesSaga = function* () {
  yield takeLatest( IMPORT_STORIES, importStories);
};

const sagas = function* () {
  yield all([
    fork(fetchEstimateSaga),
    fork(fetchImportHistorySaga),
    fork(importStoriesSaga)
  ]);
};

export default sagas;
export {
  getUserId,
  _getFetchParams,
  handleFetchEstimateError,
  fetchEstimate,
  fetchImportHistory,
  importStories,
  fetchEstimateSaga,
  fetchImportHistorySaga,
  importStoriesSaga
};
